import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
} from 'react'
import Head from 'next/head'
import type {
  NextPage,
  GetStaticProps,
  InferGetStaticPropsType,
  GetStaticPaths,
} from 'next'

import { useSelect } from 'store/index'

import { PageHistoryContext } from 'context/PageHistoryContext'

import useAugmentedRouter from 'hooks/useAugmentedRouter'
import useFuse from 'hooks/useFuse'

import Post from 'components/Blog/BlogPostCard'
import FeaturedPost from 'components/Blog/FeaturedPost'
import FeaturedDownload from 'components/Blog/FeaturedDownload'
import Pagination from 'components/Pagination/Pagination'
import BlogSearchFilter from 'components/Blog/BlogSearchFilter'
import HubspotCta from 'components/Blog/Cta/HubspotCta'
import FormstackCta from 'components/Blog/Cta/FormstackCta'
import InternalCta from 'components/Blog/Cta/InternalCta'
import Header from 'components/Header/Header'
import Footer from 'components/Footer/Footer'
import Gtm from 'components/scripts/Gtm'

import style from './Blog.module.scss'

import { BASE_WP_API_URL } from 'config/cms'
import { postsPerPage } from 'config/Blog'

import { isOverviewPage, getCtaIsFullWidth } from 'utils/Blog'
import { CollectionPageStructuredData } from 'utils/StructuredData'
import {
  fetchFooterData,
  fetchGlobalOptionsData,
  fetchWordpressPosts,
  fetchFeaturedDownloadCta,
  fetchCategoryData,
  fetchFeaturedPosts,
} from 'utils/staticData'
import { fetchCtas } from 'utils/staticData'

type BlogHomeProps = {
  footerData: any
  postsData: any[]
  totalPosts?: number
  totalPages?: number
  featuredDownloadCta: any
  categoryData: any
  featuredCategoryData: any
  featuredPostsData: any
  path: string
  categoryName: string
  ctas: any
  lastModifiedDate: string
}

export const getStaticPaths: GetStaticPaths = async () => {
  const categories = await fetchCategoryData()
  const featuredCategories = categories.filter(
    (category) => 'acf' in category && category.acf.featured_category,
  )
  return {
    paths: [
      {
        params: {
          category: null,
        },
      },
      ...featuredCategories.map((category) => ({
        params: { category: [category.slug] },
      })),
    ],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<BlogHomeProps> = async (
  context,
) => {
  const footerData = await fetchFooterData()
  const globalOptionsData = await fetchGlobalOptionsData()
  const posts = await fetchWordpressPosts()
  const featuredDownloadCta = await fetchFeaturedDownloadCta()
  const categories = await fetchCategoryData()
  const featuredCategories = categories.filter(
    (category) => 'acf' in category && category.acf.featured_category,
  )
  const featuredPostsCategory = categories.filter(
    (category) => category.slug === 'featured',
  )
  const featuredPosts = await fetchFeaturedPosts(featuredPostsCategory[0].id)
  const ctas = await fetchCtas()

  return {
    props: {
      footerData: {
        menus: footerData,
        options: globalOptionsData.acf.global_footer_group,
      },
      postsData: posts?.data,
      totalPosts: posts?.metadata.numPosts,
      totalPages: posts?.metadata.numPages,
      featuredDownloadCta: featuredDownloadCta,
      categoryData: categories,
      featuredCategoryData: featuredCategories,
      featuredPostsData: featuredPosts,
      path: context.params?.category?.[0] ?? 'blog',
      categoryName: context.params?.category?.[0]
        ? featuredCategories.find(
            ({ slug }) => slug === context.params?.category?.[0],
          )?.name
        : 'All Posts',
      ctas: ctas,
      lastModifiedDate: new Date().toString(),
    },
    revalidate: process.env.DEFAULT_REVALIDATION_PERIOD
      ? parseInt(process.env.DEFAULT_REVALIDATION_PERIOD)
      : undefined,
  }
}

const BlogHome: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  footerData,
  postsData,
  totalPosts,
  totalPages,
  featuredDownloadCta,
  categoryData,
  featuredCategoryData,
  featuredPostsData,
  path,
  categoryName,
  ctas,
}) => {
  // SEO
  const title = 'Blog'
  const metaDescription = ''
  const canonicalLink = ''

  const router = useAugmentedRouter()

  const { pageHistory } = useContext(PageHistoryContext)

  const overviewPage = isOverviewPage(path, router.query)

  const width = useSelect((state) => state.uiState.width)

  const [searchInputVal, setSearchInputVal] = useState('')

  const getPageCount = (posts: any[]) =>
    posts.length / postsPerPage <= 1
      ? 1
      : Math.ceil(posts.length / postsPerPage - 1)

  const { hits, query, onSubmitSearch, setQuery } = useFuse(postsData, {
    keys: ['renderedTitle', 'content.rendered'],
    ignoreLocation: true,
    threshold: 0,
  })

  const filteredPosts = useMemo(() => {
    const uniqueIds = {}
    return postsData.filter((post) => {
      if (categoryName === 'All Posts') {
        return true
      } else if (
        post.formatted_categories.includes(categoryName) &&
        !uniqueIds[post.id]
      ) {
        uniqueIds[post.id] = post
        return true
      } else return false
    })
  }, [categoryName, postsData])

  const posts = useMemo(
    () =>
      (query ? hits : filteredPosts)
        .slice(
          router.query.page ? parseInt(router.query.page as string) * 12 : 0,
          router.query.page
            ? parseInt(router.query.page as string) * 12 + 12
            : 12,
        )
        .map((item) => (query ? item.item : item)),
    [router.query.page, query, hits, filteredPosts],
  )

  const updatedTotalPages = useMemo(
    () =>
      query
        ? getPageCount(hits)
        : filteredPosts
        ? getPageCount(filteredPosts)
        : null,
    [hits, query, filteredPosts],
  )

  const updatedTotalItems = useMemo(
    () =>
      query ? hits.length : filteredPosts ? filteredPosts.length : totalPosts,
    [query, hits.length, filteredPosts, totalPosts],
  )

  const createURL = useCallback(
    (page: number) => ({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: page,
      },
    }),
    [router.pathname, router.query],
  )

  useEffect(() => {
    if (query && query !== router.query.search) {
      router.push(
        {
          pathname: '/blog',
          query: {
            ...(query && { search: query }),
          },
        },
        undefined,
        {
          shallow: path === 'blog' ? true : false,
        },
      )
    }
  }, [query, path, router])

  useEffect(() => {
    if (router.query.search) {
      onSubmitSearch(router.query.search as string)
    }
  }, [router.query.search, onSubmitSearch])

  useEffect(() => {
    let pageName: string
    if (router.query.search) {
      pageName = 'Blog Search Results'
    } else if (router.query.category) {
      pageName = 'Blog ' + categoryName
    } else {
      pageName = 'Blog Home'
    }
    // first check if history has updated, then if on first page allow default referrer, otherwise pass previous page to referrer
    if (
      pageHistory[pageHistory.length - 1]?.split('?')[0] ===
      window.location.href.split('?')[0]
    ) {
      if (pageHistory.length === 1) {
        window.analytics?.page(pageName)
      } else {
        window.analytics?.page(pageName, {
          referrer: pageHistory[pageHistory.length - 2],
        })
      }
    }
  }, [router.query, categoryName, pageHistory])

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta content={metaDescription} name="description"></meta>
        <link href={canonicalLink} rel="canonical" />
        <link
          href={`${BASE_WP_API_URL}/wp-content/uploads/elementor/css/post-${featuredDownloadCta.id}.css`}
          rel="stylesheet"
        />
      </Head>
      <Gtm pageType="blog overview page" />
      <Header />
      <div className={style.blogWrapper}>
        <BlogSearchFilter
          featuredCategoryData={featuredCategoryData}
          onSubmitSearch={onSubmitSearch}
          path={path}
          searchInputVal={searchInputVal}
          setQuery={setQuery}
          setSearchInputVal={setSearchInputVal}
        />
        <div
          className={`${style.blog} ${ctas && ctas.length ? style.hasCta : ''}`}
        >
          <div>
            {overviewPage && (
              <div className={style.featuredPosts}>
                <h2>Featured Posts</h2>
                <div className={style.featuredPostsContainer}>
                  {featuredPostsData && featuredPostsData.length && (
                    <FeaturedPost
                      categoryData={categoryData}
                      data={featuredPostsData[0]}
                      topFeaturedPost={true}
                    />
                  )}
                  {featuredPostsData && featuredPostsData.length && (
                    <ul>
                      {featuredPostsData.map((post, i) => {
                        if (i > 0) {
                          return (
                            <li key={i}>
                              <FeaturedPost
                                categoryData={categoryData}
                                data={post}
                                topFeaturedPost={false}
                              />
                            </li>
                          )
                        } else {
                          return null
                        }
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}
            <div className={style.posts}>
              {posts && posts.length ? (
                <>
                  {overviewPage ? (
                    <h2 className={style.trendingArticles}>
                      Trending Articles
                    </h2>
                  ) : (
                    <h2>
                      {query ? `Posts matching "${query}"` : categoryName}
                    </h2>
                  )}

                  <ul className={overviewPage ? style.overviewPage : ''}>
                    {posts.map((post, i) => {
                      if (overviewPage && i === 10) {
                        return null
                      } else
                        return (
                          <li className={style.post} key={i}>
                            <Post categoryData={categoryData} data={post} />
                          </li>
                        )
                    })}
                    {overviewPage && featuredDownloadCta?.cta.content && (
                      <li
                        className={style.featuredDownload}
                        style={{
                          backgroundImage:
                            featuredDownloadCta.cta.backgroundImg,
                        }}
                      >
                        <FeaturedDownload data={featuredDownloadCta} />
                      </li>
                    )}
                  </ul>
                </>
              ) : (
                <div>
                  <h2>No posts found</h2>
                  <p style={{ fontWeight: 300 }}>
                    We didn&apos;t find any posts that match your search query.
                    Try searching again.
                  </p>
                </div>
              )}
            </div>
          </div>
          {postsData &&
            postsData.length &&
            (updatedTotalPages || totalPages) &&
            totalPosts && (
              <Pagination
                createURL={createURL}
                itemsPerPage={postsPerPage}
                page={
                  router.query.page ? parseInt(router.query.page as string) : 1
                }
                totalItems={updatedTotalItems ?? totalPosts}
                totalPages={
                  updatedTotalPages ?? totalPages ?? getPageCount(postsData)
                }
              />
            )}
        </div>

        {ctas && ctas.length ? (
          <div className={style.homeCtaSection} id="download">
            {ctas.map((cta, i) => {
              if (cta.form_config.form_source === 'hubspot') {
                return (
                  <HubspotCta
                    cta={cta}
                    fullWidth={getCtaIsFullWidth(width, ctas.length, i)}
                    key={i}
                    pageName={'Blog'}
                  />
                )
              } else if (cta.form_config.form_source === 'formstack') {
                return (
                  <FormstackCta
                    cta={cta}
                    fullWidth={getCtaIsFullWidth(width, ctas.length, i)}
                    key={i}
                    pageName={'Blog'}
                  />
                )
              } else if (cta.form_config.form_source === 'internal') {
                return (
                  <InternalCta
                    cta={cta}
                    fullWidth={getCtaIsFullWidth(width, ctas.length, i)}
                    key={i}
                  />
                )
              }
              return null
            })}
          </div>
        ) : null}
        <CollectionPageStructuredData
          about="A collection of blog posts from Evolve Vacation Rental"
          description="Evolve Vacation Rental Blog"
          image={`${process.env.NEXT_PUBLIC_ORIGIN}/assets/logos/evolve-logo.png`}
          language="English"
          name="Blog"
        />
      </div>
      <Footer footerData={footerData} />
    </>
  )
}

export default BlogHome
